import { useCallback } from 'react';

import Text from '@zenbusiness/common-components/Text';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { NextButton } from '#client/pages/EducationStudio/components/Interactive/WorksheetActivity/worksheetComponents';
import { useLessonStorage } from '#client/pages/EducationStudio/hooks';
import { useBreakpoints } from '#client/pages/EducationStudio/hooks/useBreakpoints';

import BubblePicker from '../../BubblePicker';

import { ExercisePage } from './exercisePage';

const NEXT_BUTTON_TEXT = 'Next';
const BUBBLE_PICKER_MAX_SELECTION = 3;
export const WHOS_YOUR_VIBE = 'whosYourVibe';

export const WhosYourVibe = ({ onNext }) => {
  const { lesson } = useParams();
  const { getStorageValue, setStorageValue, saveChanges } = useLessonStorage(lesson);
  const {
    sizes: { isMobile }
  } = useBreakpoints();

  const activityOptions = [
    {
      title: 'Arti',
      text: 'The Curious Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise2.svg',
      color: '#FFB548'
    },
    {
      title: 'Niko',
      text: 'The Ambitious Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise3.svg',
      color: '#70ECC0'
    },
    {
      title: 'Kymm',
      text: 'The Empowered Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise4.svg',
      color: '#EE94B8'
    },
    {
      title: 'Porki',
      text: 'The Disruptive Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise5.svg',
      color: '#33F28B'
    },
    {
      title: 'Nurgle',
      text: 'The Courageous Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise6.svg',
      color: '#F8645B'
    },
    {
      title: 'Urll',
      text: 'The Charasmatic Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise7.svg',
      color: '#5FDDE0'
    },
    {
      title: 'Floris',
      text: 'The Sensitive Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise8.svg',
      color: '#BD8AE0'
    },
    {
      title: 'Tallyo',
      text: 'The Fiery Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise9.svg',
      color: '#C431C3'
    },
    {
      title: 'Jymm',
      text: 'The Orderly Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise10.svg',
      color: '#FA8072'
    },
    {
      title: 'Watts',
      text: 'The Existential Creator',
      image: '/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise11.svg',
      color: '#6585F7'
    }
  ];

  const isEmpty = (value) => value === '';

  const getStorageProp = (prop) => {
    const value = getStorageValue(prop);
    if (isEmpty(value)) return [];
    return value;
  };

  const handleBubbleSelection = (option) => {
    const selectedBubbles = getStorageProp(WHOS_YOUR_VIBE);
    if (selectedBubbles.some((selected) => selected.text === option.text)) {
      setStorageValue(WHOS_YOUR_VIBE, selectedBubbles.filter(
        (value) => value.text !== option.text
      ));
    } else if (selectedBubbles.length < BUBBLE_PICKER_MAX_SELECTION) {
      setStorageValue(WHOS_YOUR_VIBE, [...selectedBubbles, option]);
    }
  };

  const onNextHandler = useCallback(() => {
    saveChanges();
    onNext();
  }, [saveChanges, onNext]);

  const summary = (
    <Text color={isMobile ? 'focus00' : 'focus90'} size="lg">
      <strong>Pick the 3 archetypes that best describe you. </strong>
      We’ll keep these in mind as you navigate your Journey to Success.
    </Text>
  );

  return (
    <ExercisePage
      caption="Exercise"
      title="Who&apos;s your vibe?"
      summary={summary}
      image="/journey%20to%20success/01%20MVE%20Series/10%20INSIDE/mve_inside_exercise1.svg"
      buttonText={NEXT_BUTTON_TEXT}
      isButtonDisabled={getStorageProp(WHOS_YOUR_VIBE).length < BUBBLE_PICKER_MAX_SELECTION}
      onNext={onNextHandler}
    >
      <BubblePicker
        options={activityOptions}
        selectedBubbles={getStorageProp(WHOS_YOUR_VIBE)}
        onClick={handleBubbleSelection}
        completed={getStorageProp(WHOS_YOUR_VIBE).length >= BUBBLE_PICKER_MAX_SELECTION}
        singleColumn
      />
      {!isMobile && (
      <NextButton
        sx={{ float: 'right' }}
        variant="contained"
        color="primary"
        size="large"
        disabled={getStorageProp(WHOS_YOUR_VIBE).length < BUBBLE_PICKER_MAX_SELECTION}
        onClick={() => onNextHandler()}
      >
        {NEXT_BUTTON_TEXT}
      </NextButton>
      )}
    </ExercisePage>
  );
};

WhosYourVibe.propTypes = {
  onNext: PropTypes.func
};

WhosYourVibe.defaultProps = {
  onNext: () => {}
};

export default WhosYourVibe;
